/**
 * @file KeycloakContext.jsx
 * @description Handles authorization though keycloak and passes relevant info and functions to components
 *
 * @version 1.0.0
 * @author Carmelino Galang
 * @copyright Copyright (c) 2024, NetCyberOps LLC
 */

import {createContext, useState, useEffect, useCallback} from "react";

// KEYCLOAK
import React from "react";
import Keycloak from "keycloak-js";
import {useScope} from "@context/ScopeContext.jsx";
import Maintenance from "@sections/Maintenance/Maintenance.jsx";

//const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes

const client = new Keycloak("/keycloak.json");

const API_URL = import.meta.env.VITE_API_URL ?? "https://api.netcyberops.com/";

// Keycloak shares the DB, so check it before redirecting. An unreachable API counts as down.
const databaseDown = async () => {
    try {
        const response = await fetch(`${API_URL}v1/health/ready`, {signal: AbortSignal.timeout(5000)});
        if (response.ok) return false;
        return (await response.json()).database === "not ready";
    } catch {
        return true;
    }
};

// A DB outage returns 400 or 500; other 4xx are real errors.
const outageStatus = (status) => status === 400 || status >= 500;

const KeycloakContext = createContext();

const KeycloakContextProvider = (props) => {
    const {selectedScope} = useScope();
    const [keycloakValue, setKeycloakValue] = useState(null);
    const [authenticated, setAuthenticated] = useState(false);
    const [token, setToken] = useState(null);
    const [user, setUser] = useState(null);
    const [userPermissions, setUserPermissions] = useState(null);
    const [userType, setUserType] = useState(null);
    const [serviceLevel, setServiceLevel] = useState(null); // "managed" | "tool_only" | null
    const [userFeatures, setUserFeatures] = useState(false);
    const [featuresScopePath, setFeaturesScopePath] = useState(undefined);
    const [profilePicURL, setProfilePicURL] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userInfoUnavailable, setUserInfoUnavailable] = useState(false);
    const [userFeaturesUnavailable, setUserFeaturesUnavailable] = useState(false);
    const [retryingConnection, setRetryingConnection] = useState(false);
    const [databaseUnavailable, setDatabaseUnavailable] = useState(false);

    // Token refresh failed: show maintenance if the DB is down, otherwise log in again as before.
    const loginOrMaintenance = useCallback(async (keycloak) => {
        if (await databaseDown()) setDatabaseUnavailable(true);
        else keycloak.login();
    }, []);
    // const [csrfToken, setCsrfToken] = useState(null);

    // Used to make api calls
    const updateTokenAndFetch = useCallback(
        async (url, options) => {
            const fullUrl = `${API_URL}v1${url}`;

            const scopeHeaders = selectedScope?.path
                ? {"X-Acting-Scope": selectedScope.path}
                : {};

            try {
                await keycloakValue.updateToken(30);
            } catch {
                await loginOrMaintenance(keycloakValue);
                return;
            }

            options.headers = {
                ...options.headers,
                Authorization: `Bearer ${keycloakValue.token}`,
                ...scopeHeaders,
            };

            try {
                return await fetch(fullUrl, options);
            } catch {
                throw "Unable to connect to the server. Please contact your NetCyberOps administrator.";
            }
        },
        [keycloakValue, selectedScope, loginOrMaintenance]
    );

    // api call to get user info
    const getUserInfo = useCallback(async () => {
        try {
            const response = await updateTokenAndFetch(`/users/me`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            });
            if (!response) return;
            if (response.ok) {
                const data = await response.json();
                setUser(data.user);
                setServiceLevel(data.user?.customer?.service_level ?? null);
                setUserInfoUnavailable(false);
                // console.log("User info", data);
            } else if (response.status === 401) {
                keycloakValue.login();
            } else if (outageStatus(response.status)) {
                setUserInfoUnavailable(true);
            } else {
                const data = await response.json();
                alert(`An error occurred: ${data.message} (Error ID: ${data.error_id})`);
            }
        } catch {
            // network failure, CORS-less gateway page, or non-JSON body
            setUserInfoUnavailable(true);
        }
    }, [updateTokenAndFetch, keycloakValue]);

    // api call to get user feautres
    const getUserFeatures = useCallback(async () => {
        const scopePath = selectedScope?.path ?? null;
        try {
            const response = await updateTokenAndFetch(`/users/me/features`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            });
            if (!response) return;
            if (response.ok) {
                const data = await response.json();
                setUserPermissions(new Set(data.permissions));
                setUserFeatures(new Set(data.features));
                setUserType(data["user_type"]);
                setFeaturesScopePath(scopePath);
                setUserFeaturesUnavailable(false);
            } else if (response.status === 401) {
                keycloakValue.login();
            } else if (outageStatus(response.status)) {
                setUserFeaturesUnavailable(true);
            } else {
                const data = await response.json();
                alert(`An error occurred: ${data.message} (Error ID: ${data.error_id})`);
                window.location.href = "/error";
            }
        } catch {
            setUserFeaturesUnavailable(true);
        }
    }, [updateTokenAndFetch, keycloakValue, selectedScope]);

    // Initialize KC
    // const setKeycloack = useCallback(async () => {
    //     client
    //         .init({
    //             checkLoginIframe: false,
    //             onLoad: "login-required",
    //             refreshToken: true,
    //             pkceMethod: 'S256', // Recommended for modern security
    //             // checkLoginIframeInterval: 100
    //         })
    //         .then(function (authenticated) {
    //             setAuthenticated(authenticated);
    //             setKeycloakValue(client);
    //             setToken(client.token);
    //         })
    //         .catch((error) => {
    //             window.location.href = "/error";
    //             // console.log("keycloak error", error);
    //         });
    // }, []);

    const setKeycloack = useCallback(async () => {
        try {
            if (await databaseDown()) {
                setDatabaseUnavailable(true);
                return;
            }
            const authenticated = await client.init({
                checkLoginIframe: false,
                onLoad: "login-required",
                refreshToken: true,
                pkceMethod: "S256",
            });
            setAuthenticated(authenticated);
            setKeycloakValue(client);
            setToken(client.token);
        } catch {
            window.location.href = "/error";
        } finally {
            setLoading(false);
        }
    }, []);


    // Set up periodic token refresh
    useEffect(() => {
        if (!keycloakValue) return; // No Keycloak instance available yet

        const tokenRefreshInterval = setInterval(() => {
            // console.log("Token refresh check running..");

            keycloakValue
                .updateToken(30) // Refresh if token will expire in the next 30 seconds
                .then((refreshed) => {
                    // console.log("Token.........", refreshed);
                    if (refreshed) {
                        setToken(keycloakValue.token); // Save the updated token
                        // console.log("Token successfully refreshed");
                    }
                })
                .catch(() => {
                    // Redirect to log in if refresh fails (idle timeout), unless the DB is down
                    loginOrMaintenance(keycloakValue);
                });
        }, 30000); // Check every 30 seconds

        // Cleanup interval on unmount
        return () => clearInterval(tokenRefreshInterval);
    }, [keycloakValue, loginOrMaintenance]);

    // Set up idle timeout
    // useEffect(() => {
    //     if (!keycloakValue) return;
    //
    //     let timeout; // Store the timeout ID for activity tracking
    //
    //     const resetTimeout = () => {
    //         // Clear the existing timeout
    //         if (timeout) clearTimeout(timeout);
    //
    //         // Set a new timeout for the idle period
    //         timeout = setTimeout(() => {
    //             // console.log("User is idle for 30 minutes. Logging out...");
    //             keycloakValue.logout({redirectUri: window.location.origin});
    //             setKeycloack(null);
    //             setAuthenticated(false);
    //         }, IDLE_TIMEOUT);
    //     };
    //
    //     const events = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart'];
    //     // Attach listeners to user activity events
    //     events.forEach(event => window.addEventListener(event, resetTimeout));
    //
    //     // Initialize the timeout
    //     resetTimeout();
    //     // Clean up listeners and timeout on unmount
    //     return () => {
    //         if (timeout) clearTimeout(timeout);
    //         events.forEach(event => window.removeEventListener(event, resetTimeout));
    //     };
    // }, [keycloakValue]);

    const retryConnection = useCallback(async () => {
        // Keycloak never finished (or lost) its session: start over from scratch.
        if (databaseUnavailable) {
            window.location.reload();
            return;
        }
        setRetryingConnection(true);
        try {
            await Promise.all([getUserInfo(), getUserFeatures()]);
        } finally {
            setRetryingConnection(false);
        }
    }, [getUserInfo, getUserFeatures, databaseUnavailable]);

    const logout = async () => {
        if (keycloakValue) {
            await keycloakValue.logout({
              redirectUri: window.location.origin,
            });
          }
    };

    useEffect(() => {
        if (!keycloakValue) setKeycloack();
    }, [setKeycloack, keycloakValue]);

    useEffect(() => {
        if (token && keycloakValue) getUserInfo()
    }, [keycloakValue, getUserInfo, selectedScope]);

    useEffect(() => {
        if (token && keycloakValue) getUserFeatures();
    }, [getUserFeatures, keycloakValue, selectedScope]);

    const backendUnavailable = databaseUnavailable || userInfoUnavailable || userFeaturesUnavailable;

    return (
        <KeycloakContext.Provider
            value={{
                keycloakValue,
                authenticated,
                logout,
                getUserInfo,
                token,
                user,
                updateTokenAndFetch,
                userPermissions: userPermissions,
                hasNoAccess: userPermissions !== null && userPermissions.size === 0,
                userType,
                userFeatures: userFeatures,
                featuresReady: Boolean(userFeatures) && featuresScopePath === (selectedScope?.path ?? null),
                serviceLevel,
                profilePicURL,
                setProfilePicURL
            }}
        >
            {backendUnavailable ? (
                <Maintenance onRetry={retryConnection} retrying={retryingConnection}/>
            ) : (
                props["children"]
            )}
        </KeycloakContext.Provider>
    );
};

export {KeycloakContextProvider, KeycloakContext};
